import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AppLayout } from "./components/layout/AppLayout";
import { PageErrorBoundary } from "./components/PageErrorBoundary";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { AdminLayout } from "./features/admin/AdminLayout";
import { FeatureRoute } from "./features/feature-gates/FeatureGate";
import { DocumentTitle } from "./components/DocumentTitle";

const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage })),
);
const LoginRedirectPage = lazy(() =>
  import("./pages/LoginRedirectPage").then((m) => ({
    default: m.LoginRedirectPage,
  })),
);
const AuthBootstrapPage = lazy(() =>
  import("./pages/AuthBootstrapPage").then((m) => ({
    default: m.AuthBootstrapPage,
  })),
);
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((m) => ({
    default: m.OnboardingPage,
  })),
);
const FlowPipelinesPage = lazy(() =>
  import("./pages/flow/FlowPipelinesPage").then((m) => ({
    default: m.FlowPipelinesPage,
  })),
);
const FlowDashboardPage = lazy(() =>
  import("./pages/flow/FlowDashboardPage").then((m) => ({
    default: m.FlowDashboardPage,
  })),
);
const FlowSchedulePage = lazy(() =>
  import("./pages/flow/FlowSchedulePage").then((m) => ({
    default: m.FlowSchedulePage,
  })),
);
const FormsListPage = lazy(() =>
  import("./pages/forms/FormsListPage").then((m) => ({
    default: m.FormsListPage,
  })),
);
const FormEditor = lazy(() =>
  import("./features/forms/FormEditor").then((m) => ({
    default: m.FormEditor,
  })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const IntuitConnectPage = lazy(() =>
  import("./pages/connectors/IntuitConnectPage").then((m) => ({ default: m.IntuitConnectPage })),
);
const QuickBooksTimeConnectPage = lazy(() =>
  import("./pages/connectors/QuickBooksTimeConnectPage").then((m) => ({ default: m.QuickBooksTimeConnectPage })),
);
const IntuitDisconnectedPage = lazy(() =>
  import("./pages/connectors/IntuitDisconnectedPage").then((m) => ({ default: m.IntuitDisconnectedPage })),
);
const PublicFormPage = lazy(() =>
  import("./pages/forms/PublicFormPage").then((m) => ({
    default: m.PublicFormPage,
  })),
);
const ActionTablesPage = lazy(() =>
  import("./pages/action-tables/ActionTablesPage").then((m) => ({
    default: m.ActionTablesPage,
  })),
);
const ActionTableViewerPage = lazy(() =>
  import("./pages/action-tables/ActionTableViewerPage").then((m) => ({
    default: m.ActionTableViewerPage,
  })),
);
const ActionTableEditorPage = lazy(() =>
  import("./pages/action-tables/ActionTableEditorPage").then((m) => ({
    default: m.ActionTableEditorPage,
  })),
);
const TeamsPage = lazy(() =>
  import("./pages/teams/TeamsPage").then((m) => ({
    default: m.TeamsPage,
  })),
);
const TeamDetailPage = lazy(() =>
  import("./pages/teams/TeamDetailPage").then((m) => ({
    default: m.TeamDetailPage,
  })),
);
const BillingPage = lazy(() =>
  import("./pages/billing/BillingPage").then((m) => ({
    default: m.BillingPage,
  })),
);
const DeploymentsPage = lazy(() =>
  import("./pages/deployments/DeploymentsPage").then((m) => ({ default: m.DeploymentsPage })),
);
const AdminLoginPage = lazy(() =>
  import("./pages/admin/AdminLoginPage").then((m) => ({
    default: m.AdminLoginPage,
  })),
);
const AdminDashboardPage = lazy(() =>
  import("./pages/admin/AdminDashboardPage").then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const AdminCustomersPage = lazy(() =>
  import("./pages/admin/AdminCustomersPage").then((m) => ({
    default: m.AdminCustomersPage,
  })),
);
const AdminTenantDetailPage = lazy(() =>
  import("./pages/admin/AdminTenantDetailPage").then((m) => ({
    default: m.AdminTenantDetailPage,
  })),
);
const AdminHealthPage = lazy(() =>
  import("./pages/admin/AdminHealthPage").then((m) => ({
    default: m.AdminHealthPage,
  })),
);
const AdminPlatformAdminsPage = lazy(() =>
  import("./pages/admin/AdminPlatformAdminsPage").then((m) => ({
    default: m.AdminPlatformAdminsPage,
  })),
);
const AdminBillingDashboardPage = lazy(() =>
  import("./pages/admin/billing/AdminBillingDashboardPage").then((m) => ({
    default: m.AdminBillingDashboardPage,
  })),
);
const AdminProductsPage = lazy(() =>
  import("./pages/admin/billing/AdminProductsPage").then((m) => ({
    default: m.AdminProductsPage,
  })),
);
const AdminPricingPage = lazy(() =>
  import("./pages/admin/billing/AdminPricingPage").then((m) => ({
    default: m.AdminPricingPage,
  })),
);
const AdminTrialsPage = lazy(() =>
  import("./pages/admin/billing/AdminTrialsPage").then((m) => ({
    default: m.AdminTrialsPage,
  })),
);
const AdminAppTemplatesPage = lazy(() =>
  import("./pages/admin/AdminAppTemplatesPage").then((m) => ({
    default: m.AdminAppTemplatesPage,
  })),
);
const AdminAppTemplateEditorPage = lazy(() =>
  import("./pages/admin/AdminAppTemplateEditorPage").then((m) => ({
    default: m.AdminAppTemplateEditorPage,
  })),
);
const TemplatesPage = lazy(() =>
  import("./pages/templates/TemplatesPage").then((m) => ({
    default: m.TemplatesPage,
  })),
);
const TemplateDetailPage = lazy(() =>
  import("./pages/templates/TemplateDetailPage").then((m) => ({
    default: m.TemplateDetailPage,
  })),
);
const ProvisionPage = lazy(() =>
  import("./pages/templates/ProvisionPage").then((m) => ({
    default: m.ProvisionPage,
  })),
);
const OrganizationAdminPage = lazy(() =>
  import("./pages/organization-admin/OrganizationAdminPage").then((m) => ({
    default: m.OrganizationAdminPage,
  })),
);
const XeroRecipePage = lazy(() =>
  import("./pages/templates/XeroRecipePage").then((m) => ({ default: m.XeroRecipePage })),
);

export default function App() {
  return (
    <BrowserRouter>
      <DocumentTitle />
      <Suspense fallback={<LoadingSpinner label="Loading page" size="lg" />}>
        <Routes>
          <Route
            index
            element={
              <PageErrorBoundary>
                <LandingPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="login"
            element={
              <PageErrorBoundary>
                <LoginRedirectPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="auth/bootstrap"
            element={
              <PageErrorBoundary>
                <AuthBootstrapPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="onboarding"
            element={
              <PageErrorBoundary>
                <OnboardingPage />
              </PageErrorBoundary>
            }
          />

          {/* Public QuickBooks Online (Intuit) connect-link pages — no Grid2
              login, no app shell. URLs are registered in the Intuit portal. */}
          <Route
            path="connectors/intuit/connect"
            element={
              <PageErrorBoundary>
                <IntuitConnectPage />
              </PageErrorBoundary>
            }
          />
          {/* Public QuickBooks Time (classic app) connect-link landing page. */}
          <Route
            path="connectors/quickbooks-time/connect"
            element={
              <PageErrorBoundary>
                <QuickBooksTimeConnectPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="connectors/intuit/disconnected"
            element={
              <PageErrorBoundary>
                <IntuitDisconnectedPage />
              </PageErrorBoundary>
            }
          />

          {/* Public form routes — no app shell or sidebar.
              /forms/:formId/view is the Action Tables launch URL with row/table context. */}
          <Route
            path="forms/public/:formId"
            element={
              <PageErrorBoundary>
                <PublicFormPage />
              </PageErrorBoundary>
            }
          />
          <Route
            path="forms/:formId/view"
            element={
              <PageErrorBoundary>
                <PublicFormPage />
              </PageErrorBoundary>
            }
          />

          <Route element={<AppLayout />}>
            <Route
              path="dashboard"
              element={
                <FeatureRoute id="app.dashboard" title="Dashboard is not available">
                  <PageErrorBoundary>
                    <DashboardPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="flow"
              element={
                <FeatureRoute id="flow.module" title="Flow is not available">
                  <PageErrorBoundary>
                    <FlowPipelinesPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="flow/dashboard"
              element={
                <FeatureRoute id="flow.module" title="Flow is not available">
                  <PageErrorBoundary>
                    <FlowDashboardPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="flow/schedule"
              element={
                <FeatureRoute id="flow.module" title="Flow is not available">
                  <PageErrorBoundary>
                    <FlowSchedulePage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="forms"
              element={
                <FeatureRoute id="forms.module" title="Forms are not available">
                  <PageErrorBoundary>
                    <FormsListPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="forms/:formId/edit"
              element={
                <FeatureRoute id="forms.module" title="Forms are not available">
                  <PageErrorBoundary>
                    <FormEditor />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="action-tables"
              element={
                <FeatureRoute id="action_tables.module" title="Action Tables are not available">
                  <PageErrorBoundary>
                    <ActionTablesPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="action-tables/new"
              element={
                <FeatureRoute id="action_tables.module" title="Action Tables are not available">
                  <PageErrorBoundary>
                    <ActionTableEditorPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="action-tables/:tableId"
              element={
                <FeatureRoute id="action_tables.module" title="Action Tables are not available">
                  <PageErrorBoundary>
                    <ActionTableViewerPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="action-tables/:tableId/edit"
              element={
                <FeatureRoute id="action_tables.module" title="Action Tables are not available">
                  <PageErrorBoundary>
                    <ActionTableEditorPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="settings"
              element={
                <PageErrorBoundary>
                  <SettingsPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="teams"
              element={
                <FeatureRoute id="app.teams" title="Teams are not available">
                  <PageErrorBoundary>
                    <TeamsPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="teams/:teamId"
              element={
                <FeatureRoute id="app.teams" title="Teams are not available">
                  <PageErrorBoundary>
                    <TeamDetailPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="billing"
              element={
                <PageErrorBoundary>
                  <BillingPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="deployments"
              element={
                <FeatureRoute id="deployments.self_hosted" title="Self-hosted deployments are not available">
                  <PageErrorBoundary>
                    <DeploymentsPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="organization-admin"
              element={
                <PageErrorBoundary>
                  <OrganizationAdminPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="templates"
              element={
                <FeatureRoute id="templates.module" title="Templates are not available">
                  <PageErrorBoundary>
                    <TemplatesPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="templates/xero-recipe"
              element={<FeatureRoute id="templates.module" title="Templates are not available"><PageErrorBoundary><XeroRecipePage /></PageErrorBoundary></FeatureRoute>}
            />
            <Route
              path="templates/:templateId"
              element={
                <FeatureRoute id="templates.module" title="Templates are not available">
                  <PageErrorBoundary>
                    <TemplateDetailPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
            <Route
              path="templates/provisions/:provisionId"
              element={
                <FeatureRoute id="templates.module" title="Templates are not available">
                  <PageErrorBoundary>
                    <ProvisionPage />
                  </PageErrorBoundary>
                </FeatureRoute>
              }
            />
          </Route>

          {/* Admin routes — separate layout, no app shell */}
          <Route
            path="admin/login"
            element={
              <PageErrorBoundary>
                <AdminLoginPage />
              </PageErrorBoundary>
            }
          />
          <Route path="admin" element={<AdminLayout />}>
            <Route
              index
              element={
                <PageErrorBoundary>
                  <AdminDashboardPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="customers"
              element={
                <PageErrorBoundary>
                  <AdminCustomersPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="customers/:tenantId"
              element={
                <PageErrorBoundary>
                  <AdminTenantDetailPage />
                </PageErrorBoundary>
              }
            />
            <Route path="tenants" element={<Navigate to="/admin/customers" replace />} />
            <Route
              path="tenants/:tenantId"
              element={
                <PageErrorBoundary>
                  <AdminTenantDetailPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="health"
              element={
                <PageErrorBoundary>
                  <AdminHealthPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="administrators"
              element={
                <PageErrorBoundary>
                  <AdminPlatformAdminsPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="flow-templates"
              element={
                <PageErrorBoundary>
                  <AdminAppTemplatesPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="flow-templates/new"
              element={
                <PageErrorBoundary>
                  <AdminAppTemplateEditorPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="flow-templates/:templateKey"
              element={
                <PageErrorBoundary>
                  <AdminAppTemplateEditorPage />
                </PageErrorBoundary>
              }
            />
            <Route path="app-templates/*" element={<Navigate to="/admin/flow-templates" replace />} />
            <Route
              path="billing"
              element={
                <PageErrorBoundary>
                  <AdminBillingDashboardPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing/products"
              element={
                <PageErrorBoundary>
                  <AdminProductsPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing/pricing"
              element={
                <PageErrorBoundary>
                  <AdminPricingPage />
                </PageErrorBoundary>
              }
            />
            <Route
              path="billing/subscriptions"
              element={<Navigate to="/admin/customers" replace />}
            />
            <Route
              path="billing/trials"
              element={
                <PageErrorBoundary>
                  <AdminTrialsPage />
                </PageErrorBoundary>
              }
            />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
